import React, { useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Area
} from "recharts";
import "./Charts.css";

// Utility to generate chart slabs
const getUserRegistrationChartData = (groupedData, range) => {
  const chartData = [];
  const today = new Date();

  if (range === "1week") {
    // Create the last 7 days data (including today)
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const key = date.toISOString().split("T")[0]; // Key in 'YYYY-MM-DD' format
      const label = date.toLocaleDateString("en-IN", {
        weekday: "short",
        month: "short",
        day: "numeric",
      });

      chartData.push({
        name: label,
        Users: groupedData[key] || 0,
      });
    }
  } else if (range === "1month") {
    const pastDate = new Date();
    pastDate.setDate(today.getDate() - 27); // 28 days total

    // For 1 month range, create 4 weeks data
    for (let i = 0; i < 4; i++) {
      const weekStart = new Date(pastDate);
      weekStart.setDate(pastDate.getDate() + i * 7);

      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      if (weekEnd > today) weekEnd.setTime(today.getTime());

      const label = `${weekStart.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
      })} - ${weekEnd.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
      })}`;

      let count = 0;

      // Count users in the specified week range
      for (const key in groupedData) {
        const entryDate = new Date(key);
        if (entryDate >= weekStart && entryDate <= weekEnd) {
          count += groupedData[key];
        }
      }

      chartData.push({ name: label, Users: count });
    }
  } else {
    // For 3 or 6 month ranges, group by month
    const monthsToInclude = range === "3month" ? 3 : 6;
    const endMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    for (let i = monthsToInclude - 1; i >= 0; i--) {
      const month = new Date(endMonth.getFullYear(), endMonth.getMonth() - i, 1);
      const label = month.toLocaleDateString("en-IN", {
        month: "short",
        year: "numeric",
      });

      chartData.push({
        name: label,
        Users: groupedData[label] || 0,
      });
    }
  }

  return chartData;
};

const UserRegistrationChart = ({ data = [], selectedRange }) => {
  const { chartData, totalUsers } = useMemo(() => {
    console.log("Inside useMemo: data =", data);
    if (!data||data.length===0) return { chartData: [], totalUsers: 0 };

    const groupedData = {};
    let totalUsers = 0;

    // Group data based on the selected range (week/month/year)
    data.forEach((user) => {
      const date = new Date(user.date_created);
      if (isNaN(date)) return;

      let key = "";
      if (selectedRange === "1week" || selectedRange === "1month") {
        // Convert date to 'YYYY-MM-DD' format for daily or weekly group
        key = date.toISOString().split("T")[0];
      } else {
        // Group by "MMM YYYY" format for monthly data
        key = date.toLocaleDateString("en-IN", { month: "short", year: "numeric" });
      }

      groupedData[key] = (groupedData[key] || 0) + 1; // Increment user count
      totalUsers++; // Track total number of users
    });

    const chartData = getUserRegistrationChartData(groupedData, selectedRange);
    return { chartData, totalUsers };
  }, [data, selectedRange]);

  return (
    <div className="chart-box line-chart" style={{ width: "25%", flex: "1 1" }}>
      <div
        className="chart-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <h3 style={{ color: "#064a52" }}>Customer Registrations Over Time</h3>
        <span
          style={{
            fontWeight: "bold",
            fontSize: "16px",
            color: "#064a52",
          }}
        >
          Total Users: {totalUsers}
        </span>
      </div>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
        >
          <XAxis
            dataKey="name"
            interval={0}
            angle={-30}
            textAnchor="end"
            height={60}
          />
          <YAxis />
          <Tooltip />
          <Legend />
          <Area
  type="monotone"
  dataKey="Users"
  stroke="none"
  fill="rgba(126, 132, 201, 0.2)"
/>
          <Line
            type="monotone"
            dataKey="Users"
            stroke="#064a52"
            strokeWidth={2}
            dot={{ r: 3 }}
            activeDot={{ r: 6 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default UserRegistrationChart;
